'use client';

import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { queryClientAtom } from 'jotai-tanstack-query';
import { PropsWithChildren } from 'react';

import { getHealthCheckApi } from '@flipster/apis/v2/health';
import { R230522Provider } from '@flipster/features/src/r230522/provider';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { usePathname } from '@flipster/i18n/routing';
import { PathnameScope, RouterScope } from '@flipster/react/providers/route';
import { weakR230522SocketBunja } from '@flipster/socket/r230522/socket';
import { checkAxiosError } from '@flipster/utils/axios';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

const websocketOnlyPaths = [/^\/support\/contracts\/.*$/, /^\/support\/spot-pairs\/.*$/];

export const WebR230522Provider = ({ children }: PropsWithChildren) => {
  const pathname = usePathname();

  // context: https://prextech.slack.com/archives/C07BZ2P9BHR/p1756094900016749?thread_ts=1753771992.514069&cid=C07BZ2P9BHR
  // contract 페이지는 ticker 토픽에 의존하는데 r230522의 다중구독 기능 부재로 인해 브릿지를 통해 ticker를 내려받을 수 없음
  // 따라서 웹뷰 맥락이더라도 웹소켓을 사용하도록 강제함
  const preferredSocketType = websocketOnlyPaths.some((regex) => regex.test(pathname)) ? 'websocket' : 'hybrid';

  return (
    <R230522Provider preferredSocketType={preferredSocketType}>
      {children}
      <R230522Hook />
    </R230522Provider>
  );
};

const R230522Hook = () => {
  useBunja(r230522HookBunja);
  return null;
};
const r230522HookBunja = bunja(() => {
  bunja.use(checkSessionExpiredBunja);
  bunja.use(healthCheckOnSocketErrorBunja);
  bunja.use(refreshNotiBunja);
});

const checkSessionExpiredBunja = bunja(() => {
  const { r230522Socket } = bunja.use(weakR230522SocketBunja);
  const { logout } = bunja.use(authBunja);
  bunja.effect(() => r230522Socket.on('sessionExpired', logout));
});

const healthCheckOnSocketErrorBunja = bunja(() => {
  const { r230522Socket } = bunja.use(weakR230522SocketBunja);
  const pathname = bunja.use(PathnameScope);
  const router = bunja.use(RouterScope);
  if (pathname === '/maintenance') return;
  bunja.effect(() => {
    return r230522Socket.on('error', () => {
      getHealthCheckApi().catch((err) => {
        if (checkAxiosError(err) && err.status === 503) router.push('/maintenance');
      });
    });
  });
});

const refreshNotiBunja = bunja(() => {
  const { r230522Events } = bunja.use(weakR230522SocketBunja);
  const store = bunja.use(JotaiStoreScope);
  bunja.effect(() => {
    return r230522Events.on('private/noti', () => {
      const queryClient = store.get(queryClientAtom);
      queryClient.invalidateQueries({ queryKey: ['getUserNotifications'] });
    });
  });
});
